import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './flowdoRole.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import { flowdoRoleService } from '../../services/flowdoRoleService';
import { formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import { formatRoleLabel } from '../../utils/roleDescriptions';
import { useCanDo } from '../../hooks/useCanDo';

const formatDate = (value) => formatDateToDDMMMYYYY(value) || '—';

const FlowdoRoleList = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const canEditRoles = useCanDo('users', 'create_edit');
  const canDeleteRoles = useCanDo('users', 'delete');

  const notifySuccess = (message) => {
    toast.success(message, { position: 'top-right', autoClose: 5000 });
  };
  const notifyError = (message) => {
    toast.error(message, { position: 'top-right', autoClose: 5000 });
  };

  const fetchRoles = async () => {
    try {
      const res = await flowdoRoleService.list();
      setRoles(res.data?.data || []);
    } catch (err) {
      console.error('Role list error:', err);
      notifyError(err.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const roleListToolbar = useMemo(
    () =>
      canEditRoles ? (
        <button
          type="button"
          className="toolbar-add-btn"
          onClick={() => navigate('/settings/flowdoRoles/new')}
          title="Add Role"
          aria-label="Add Role"
        >
          <i className="fas fa-plus" aria-hidden="true"></i>
          <span>Add Role</span>
        </button>
      ) : null,
    [canEditRoles, navigate]
  );

  usePageToolbar(roleListToolbar, [roleListToolbar]);

  const handleDelete = async () => {
    if (!selectedRole) return;
    setDeleteLoading(true);
    try {
      await flowdoRoleService.remove(selectedRole.id);
      notifySuccess('Role deleted');
      setShowDeleteModal(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to delete role');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    { key: 'index', header: '#', accessor: (_row, index) => index + 1 },
    { key: 'name', header: 'Role', accessor: (row) => formatRoleLabel(row.name) },
    { key: 'created_at', header: 'Created Date', accessor: (row) => formatDate(row.created_at) },
    { key: 'created_by_name', header: 'Created By', accessor: (row) => row.created_by_name || '—' },
    { key: 'user_count', header: 'Users Assigned', accessor: (row) => row.user_count || 0 },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`status-badge ${row.status === 'active' ? 'status-done' : 'status-paused'}`}>
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];

  const getActions = (row) => {
    const actions = [];
    if (canEditRoles) {
      actions.push({
        label: 'Edit',
        icon: 'fas fa-edit',
        onClick: () => navigate(`/settings/flowdoRoles/${row.id}`),
      });
    }
    if (canDeleteRoles && !row.is_system && !row.user_count) {
      actions.push({
        label: 'Delete',
        icon: 'fas fa-trash',
        className: 'text-danger',
        onClick: () => {
          setSelectedRole(row);
          setShowDeleteModal(true);
        },
      });
    }
    return actions;
  };

  return (
    <Row className="flowdo-role-page" style={{ marginBottom: '0px !important' }}>
      <ToastContainer />
      <Col lg={12}>
        <Card className="user-list-card">
          <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              <div className="row">
                <CommonTable
                  columns={columns}
                  data={roles}
                  actions={getActions}
                  emptyMessage="No roles found"
                  emptyStateIcon="fas fa-user-shield"
                  getRowId={(row) => row.id}
                  maxHeight="70vh"
                  minHeight="400px"
                />
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the role "{formatRoleLabel(selectedRole?.name)}"? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <BackToTop />
      <InternalFooter />
    </Row>
  );
};

export default FlowdoRoleList;
